/**
 * Static lookup: SAP CPQ system calculation name → formula info.
 * Source: SAP CPQ 2.0 documentation + live tenant API.
 * 80 system calculations across Item, Product Type, and Total levels.
 */

const FORMULAS = {
  // ── Item One-Time Pricing ──────────────────────────────────────────

  "Item One-Time Net Price": {
    level: "Item",
    formula: "Item.NetPrice = Item.ListPrice × (1 − Item.DiscountPercent / 100)",
    description: "Derives the unit net price from list price minus the discount percentage.",
  },
  "Item One-Time Extended Amount": {
    level: "Item",
    formula: "Item.ExtendedAmount = (Item.NetPrice × Item.Quantity) + Item.TaxAmount",
    description: "Total line value: net price times quantity, plus tax.",
  },
  "Item One-Time Discount Amount (from One-Time Discount Percent)": {
    level: "Item",
    formula: "Item.DiscountAmount = Item.ListPrice × Item.Quantity × Item.DiscountPercent / 100",
    description: "Calculates discount amount from the discount percentage applied to the full line value.",
  },
  "Item One-Time Discount Amount (from One-Time Net Price)": {
    level: "Item",
    formula: "Item.DiscountAmount = Item.ListPrice − Item.NetPrice",
    description: "Derives discount amount as the difference between list price and net price (per unit).",
  },
  "Item One-Time Discount Amount (from One-Time Extended Amount)": {
    level: "Item",
    formula: "Item.DiscountAmount = (Item.ListPrice − Item.ExtendedAmount / Item.Quantity) × Item.Quantity",
    description: "Derives discount amount from the extended amount back-calculated to a unit price.",
  },
  "Item One-Time Extended Cost": {
    level: "Item",
    formula: "Item.ExtendedCost = Item.Cost × Item.Quantity",
    description: "Total cost for the line: unit cost times quantity.",
  },
  "Item One-Time Cost": {
    level: "Item",
    formula: "Item.Cost = Item.ExtendedCost / Item.Quantity",
    description: "Derives unit cost from extended cost divided by quantity.",
  },
  "Item One-Time List Price": {
    level: "Item",
    formula: "Item.ListPrice = Item.ExtendedListPrice / Item.Quantity",
    description: "Derives unit list price from extended list price divided by quantity.",
  },
  "Item One-Time Extended List Price": {
    level: "Item",
    formula: "Item.ExtendedListPrice = Item.ListPrice × Item.Quantity",
    description: "Total list value for the line: unit list price times quantity.",
  },
  "Item One-Time Gross Margin Percent": {
    level: "Item",
    formula: "Item.GrossMarginPercent = 100 × (Item.ExtendedAmount − Item.ExtendedCost) / Item.ExtendedAmount",
    description: "Gross margin as a percentage of extended amount.",
  },

  // ── Item One-Time Discount Percent (reverse / relative calcs) ─────

  "Item One-Time Discount Percent (from One-Time Discount Amount)": {
    level: "Item",
    formula: "Item.DiscountPercent = 100 × Item.DiscountAmount / Item.ListPrice",
    description: "Reverse-calculates discount percent from the discount amount.",
  },
  "Item One-Time Discount Percent (from One-Time Gross Margin Percent)": {
    level: "Item",
    formula: "Reverse: adjusts Item.DiscountPercent to achieve target Gross Margin %",
    description: "Changes item discount so that the resulting margin matches the entered gross margin percent.",
  },
  "Item One-Time Discount Percent (from Product Type One-Time Discount Percent)": {
    level: "Item → Product Type",
    formula: "Relative distribution: Item.DiscountPercent adjusted proportionally based on Item.MaxDiscountPercent",
    description: "Distributes the product type discount percent change down to items proportionally.",
  },
  "Item One-Time Discount Percent (from Product Type One-Time Discount Amount)": {
    level: "Item → Product Type",
    formula: "Relative distribution: Item.DiscountAmount = (Item.MaxDiscountAmount / Sum(All.MaxDiscountAmount)) × ΔDiscountAmount",
    description: "Distributes the product type discount amount change down to items proportionally.",
  },
  "Item One-Time Discount Percent (from Product Type One-Time Net Price)": {
    level: "Item → Product Type",
    formula: "Relative distribution: adjusts Item.DiscountPercent so PT.NetPrice matches the entered value",
    description: "Distributes a product type net price change down to item discount percentages.",
  },
  "Item One-Time Discount Percent (from Product Type One-Time Gross Margin Percent)": {
    level: "Item → Product Type",
    formula: "Relative distribution: adjusts Item.DiscountPercent so PT.GrossMarginPercent matches target",
    description: "Distributes a product type gross margin change down to item discounts.",
  },
  "Item One-Time Discount Percent (from Total One-Time Amount)": {
    level: "Item → Total",
    formula: "Relative distribution: adjusts Item.DiscountPercent so Totals.Amount matches the entered value",
    description: "Distributes a total amount change down to item discount percentages.",
  },
  "Item One-Time Discount Percent (from Total One-Time Discount Amount)": {
    level: "Item → Total",
    formula: "Relative distribution: adjusts Item.DiscountPercent so Totals.DiscountAmount matches target",
    description: "Distributes a total discount amount change down to item discounts.",
  },
  "Item One-Time Discount Percent (from Total One-Time Discount Percent)": {
    level: "Item → Total",
    formula: "Relative distribution: adjusts Item.DiscountPercent so Totals.DiscountPercent matches target",
    description: "Distributes a total discount percent change down to item discounts.",
  },
  "Item One-Time Discount Percent (from Total One-Time Net Price)": {
    level: "Item → Total",
    formula: "Relative distribution: adjusts Item.DiscountPercent so Totals.NetPrice matches the entered value",
    description: "Distributes a total net price change down to item discount percentages.",
  },
  "Item One-Time Discount Percent (from Total One-Time Gross Margin Percent)": {
    level: "Item → Total",
    formula: "Relative distribution: adjusts Item.DiscountPercent so Totals.GrossMarginPercent matches target",
    description: "Distributes a total gross margin change down to item discounts.",
  },

  // ── Item Recurring Pricing ─────────────────────────────────────────

  "Item Recurring Net Price": {
    level: "Item",
    formula: "Item.MrcNetPrice = Item.MrcListPrice − Item.MrcDiscountAmount",
    description: "Recurring unit net price: list price minus discount amount.",
  },
  "Item Recurring Extended Amount": {
    level: "Item",
    formula: "Item.MrcExtendedAmount = (Item.MrcNetPrice × Item.Quantity) + Item.MrcTaxAmount",
    description: "Total recurring line value: net price times quantity, plus recurring tax.",
  },
  "Item Recurring Discount Amount (from Recurring Discount Percent)": {
    level: "Item",
    formula: "Item.MrcDiscountAmount = Item.MrcListPrice × Item.Quantity × Item.MrcDiscountPercent / 100",
    description: "Recurring discount amount derived from the recurring discount percentage.",
  },
  "Item Recurring Discount Amount (from Recurring Net Price)": {
    level: "Item",
    formula: "Item.MrcDiscountAmount = Item.MrcListPrice − Item.MrcNetPrice",
    description: "Recurring discount derived as difference between recurring list and net price.",
  },
  "Item Recurring Discount Amount (from Recurring Extended Amount)": {
    level: "Item",
    formula: "Item.MrcDiscountAmount = Item.MrcListPrice − Item.MrcExtendedAmount / Item.Quantity",
    description: "Recurring discount amount back-calculated from the recurring extended amount.",
  },
  "Item Recurring Extended Cost": {
    level: "Item",
    formula: "Item.MrcExtendedCost = Item.MrcCost × Item.Quantity",
    description: "Total recurring cost for the line: recurring unit cost times quantity.",
  },
  "Item Recurring Cost": {
    level: "Item",
    formula: "Item.MrcCost = Item.MrcExtendedCost / Item.Quantity",
    description: "Recurring unit cost derived from recurring extended cost divided by quantity.",
  },
  "Item Recurring List Price": {
    level: "Item",
    formula: "Item.MrcListPrice = Item.MrcExtendedListPrice / Item.Quantity",
    description: "Recurring unit list price derived from extended list price divided by quantity.",
  },
  "Item Recurring Extended List Price": {
    level: "Item",
    formula: "Item.MrcExtendedListPrice = Item.MrcListPrice × Item.Quantity",
    description: "Total recurring list value: recurring list price times quantity.",
  },
  "Item Recurring Gross Margin Percent": {
    level: "Item",
    formula: "Item.MrcGrossMarginPercent = 100 × (Item.MrcExtendedAmount − Item.MrcExtendedCost) / Item.MrcExtendedAmount",
    description: "Recurring gross margin as a percentage of recurring extended amount.",
  },

  // ── Item Recurring Discount Percent (reverse / relative) ──────────

  "Item Recurring Discount Percent (from Recurring Discount Amount)": {
    level: "Item",
    formula: "Item.MrcDiscountPercent = 100 × Item.MrcDiscountAmount / Item.MrcListPrice",
    description: "Reverse-calculates recurring discount percent from the recurring discount amount.",
  },
  "Item Recurring Discount Percent (from Recurring Gross Margin Percent)": {
    level: "Item",
    formula: "Reverse: adjusts Item.MrcDiscountPercent to achieve target Recurring Gross Margin %",
    description: "Changes recurring discount so the resulting margin matches the entered target.",
  },
  "Item Recurring Discount Percent (from Product Type Recurring Discount Percent)": {
    level: "Item → Product Type",
    formula: "Relative distribution: Item.MrcDiscountPercent adjusted proportionally",
    description: "Distributes product type recurring discount percent change down to items.",
  },
  "Item Recurring Discount Percent (from Product Type Recurring Discount Amount)": {
    level: "Item → Product Type",
    formula: "Relative distribution: recurring discount amount distributed to items proportionally",
    description: "Distributes product type recurring discount amount change down to items.",
  },
  "Item Recurring Discount Percent (from Product Type Recurring Net Price)": {
    level: "Item → Product Type",
    formula: "Relative distribution: adjusts Item.MrcDiscountPercent so PT.MrcNetPrice matches target",
    description: "Distributes a product type recurring net price change down to item discounts.",
  },
  "Item Recurring Discount Percent (from Product Type Recurring Gross Margin Percent)": {
    level: "Item → Product Type",
    formula: "Relative distribution: adjusts Item.MrcDiscountPercent so PT.MrcGrossMarginPercent matches target",
    description: "Distributes a product type recurring margin change down to item discounts.",
  },
  "Item Recurring Discount Percent (from Total Recurring Amount)": {
    level: "Item → Total",
    formula: "Relative distribution: adjusts Item.MrcDiscountPercent so Totals.MrcAmount matches target",
    description: "Distributes a total recurring amount change down to item discounts.",
  },
  "Item Recurring Discount Percent (from Total Recurring Discount Amount)": {
    level: "Item → Total",
    formula: "Relative distribution: adjusts Item.MrcDiscountPercent so Totals.MrcDiscountAmount matches target",
    description: "Distributes a total recurring discount amount change down to item discounts.",
  },
  "Item Recurring Discount Percent (from Total Recurring Discount Percent)": {
    level: "Item → Total",
    formula: "Relative distribution: adjusts Item.MrcDiscountPercent so Totals.MrcDiscountPercent matches target",
    description: "Distributes a total recurring discount percent change down to item discounts.",
  },
  "Item Recurring Discount Percent (from Total Recurring Net Price)": {
    level: "Item → Total",
    formula: "Relative distribution: adjusts Item.MrcDiscountPercent so Totals.MrcNetPrice matches target",
    description: "Distributes a total recurring net price change down to item discounts.",
  },
  "Item Recurring Discount Percent (from Total Recurring Gross Margin Percent)": {
    level: "Item → Total",
    formula: "Relative distribution: adjusts Item.MrcDiscountPercent so Totals.MrcGrossMarginPercent matches target",
    description: "Distributes a total recurring margin change down to item discounts.",
  },

  // ── Rolled-Up Discount Calcs ───────────────────────────────────────

  "One-Time Discount Percent (from Rolled Up Discount Amount)": {
    level: "Item (rolled up)",
    formula: "Item.DiscountPercent recalculated from parent's rolled-up discount amount",
    description: "Recalculates item discount percent when the rolled-up discount amount on a parent BOM line changes.",
  },
  "One-Time Discount Percent (from Rolled Up Discount Percent)": {
    level: "Item (rolled up)",
    formula: "Item.DiscountPercent set from parent's rolled-up discount percent",
    description: "Recalculates item discount percent when the rolled-up discount percent on a parent BOM line changes.",
  },
  "One-Time Discount Percent (from Rolled Up Extended Amount)": {
    level: "Item (rolled up)",
    formula: "Item.DiscountPercent derived from parent's rolled-up extended amount",
    description: "Recalculates item discount percent when the rolled-up extended amount changes.",
  },
  "One-Time Discount Percent (from Rolled Up Net Price)": {
    level: "Item (rolled up)",
    formula: "Item.DiscountPercent derived from parent's rolled-up net price",
    description: "Recalculates item discount percent when the rolled-up net price changes.",
  },
  "Recurring Discount Percent (from Recurring Rolled Up Discount Amount)": {
    level: "Item (rolled up)",
    formula: "Item.MrcDiscountPercent recalculated from parent's recurring rolled-up discount amount",
    description: "Recalculates recurring discount percent from the parent's recurring rolled-up discount amount.",
  },
  "Recurring Discount Percent (from Recurring Rolled Up Discount Percent)": {
    level: "Item (rolled up)",
    formula: "Item.MrcDiscountPercent set from parent's recurring rolled-up discount percent",
    description: "Recalculates recurring discount percent from the parent's recurring rolled-up discount percent.",
  },
  "Recurring Discount Percent (from Recurring Rolled Up Extended Amount)": {
    level: "Item (rolled up)",
    formula: "Item.MrcDiscountPercent derived from parent's recurring rolled-up extended amount",
    description: "Recalculates recurring discount percent from the parent's recurring rolled-up extended amount.",
  },
  "Recurring Discount Percent (from Recurring Rolled Up Net Price)": {
    level: "Item (rolled up)",
    formula: "Item.MrcDiscountPercent derived from parent's recurring rolled-up net price",
    description: "Recalculates recurring discount percent from the parent's recurring rolled-up net price.",
  },

  // ── Product Type One-Time ──────────────────────────────────────────

  "Product Type One-Time Net Price": {
    level: "Product Type",
    formula: "PT.NetPrice = Sum(Item.ExtendedAmount)",
    description: "Rolled up from item extended amounts within this product type.",
  },
  "Product Type One-Time Discount Amount": {
    level: "Product Type",
    formula: "PT.DiscountAmount = PT.ListPrice − PT.NetPrice",
    description: "Discount amount as the difference between product type list price and net price.",
  },
  "Product Type One-Time Discount Percent": {
    level: "Product Type",
    formula: "PT.DiscountPercent = 100 × PT.DiscountAmount / PT.ListPrice",
    description: "Discount percentage derived from the product type discount amount and list price.",
  },
  "Product Type One-Time List Price": {
    level: "Product Type",
    formula: "PT.ListPrice = Sum(Item.ExtendedListPrice)",
    description: "Rolled up from item extended list prices within this product type.",
  },
  "Product Type One-Time Cost": {
    level: "Product Type",
    formula: "PT.Cost = Sum(Item.ExtendedCost)",
    description: "Rolled up from item extended costs within this product type.",
  },
  "Product Type One-Time Gross Margin Percent": {
    level: "Product Type",
    formula: "PT.GrossMarginPercent = 100 × (PT.NetPrice − PT.Cost) / PT.NetPrice",
    description: "Product type gross margin as a percentage of net price.",
  },

  // ── Product Type Recurring ─────────────────────────────────────────

  "Product Type Recurring Net Price": {
    level: "Product Type",
    formula: "PT.MrcNetPrice = Sum(Item.MrcExtendedAmount)",
    description: "Rolled up from item recurring extended amounts within this product type.",
  },
  "Product Type Recurring Discount Amount": {
    level: "Product Type",
    formula: "PT.MrcDiscountAmount = PT.MrcListPrice − PT.MrcNetPrice",
    description: "Recurring discount amount: product type recurring list price minus recurring net price.",
  },
  "Product Type Recurring Discount Percent": {
    level: "Product Type",
    formula: "PT.MrcDiscountPercent = 100 × PT.MrcDiscountAmount / PT.MrcListPrice",
    description: "Recurring discount percentage derived from recurring discount amount and list price.",
  },
  "Product Type Recurring List Price": {
    level: "Product Type",
    formula: "PT.MrcListPrice = Sum(Item.MrcExtendedListPrice)",
    description: "Rolled up from item recurring extended list prices.",
  },
  "Product Type Recurring Cost": {
    level: "Product Type",
    formula: "PT.MrcCost = Sum(Item.MrcExtendedCost)",
    description: "Rolled up from item recurring extended costs.",
  },
  "Product Type Recurring Gross Margin Percent": {
    level: "Product Type",
    formula: "PT.MrcGrossMarginPercent = 100 × (PT.MrcNetPrice − PT.MrcCost) / PT.MrcNetPrice",
    description: "Product type recurring gross margin as a percentage of recurring net price.",
  },

  // ── Total / Cart One-Time ──────────────────────────────────────────

  "Total One-Time Net Price": {
    level: "Total",
    formula: "Totals.NetPrice = Sum(Item.ExtendedAmount)",
    description: "Rolled up from all item extended amounts across the entire quote.",
  },
  "Total One-Time List Price": {
    level: "Total",
    formula: "Totals.ListPrice = Sum(Item.ExtendedListPrice)",
    description: "Rolled up from all item extended list prices.",
  },
  "Total Amount": {
    level: "Total",
    formula: "Totals.Amount = Totals.NetPrice",
    description: "The total one-time amount equals the total one-time net price.",
  },
  "Total Cost": {
    level: "Total",
    formula: "Totals.Cost = Sum(Item.ExtendedCost)",
    description: "Rolled up from all item extended costs.",
  },
  "Total Product Discount Amount": {
    level: "Total",
    formula: "Totals.ProductDiscountAmount = Totals.ListPrice − Totals.NetPrice",
    description: "Total discount amount as the difference between total list price and total net price.",
  },
  "Total Average Product Discount Percent": {
    level: "Total",
    formula: "Totals.AvgProductDiscountPercent = 100 × Totals.ProductDiscountAmount / Totals.ListPrice",
    description: "Weighted average discount percent across all items.",
  },
  "Total One-Time Gross Margin Percent": {
    level: "Total",
    formula: "Totals.GrossMarginPercent = 100 × (Totals.NetPrice − Totals.Cost) / Totals.NetPrice",
    description: "Total one-time gross margin as a percentage of total net price.",
  },

  // ── Total / Cart Recurring ─────────────────────────────────────────

  "Total Recurring Net Price": {
    level: "Total",
    formula: "Totals.MrcNetPrice = Sum(Item.MrcExtendedAmount)",
    description: "Rolled up from all item recurring extended amounts.",
  },
  "Total Recurring Amount": {
    level: "Total",
    formula: "Totals.MrcAmount = Totals.MrcNetPrice",
    description: "The total recurring amount equals the total recurring net price.",
  },
  "Total Recurring List Price": {
    level: "Total",
    formula: "Totals.MrcListPrice = Sum(Item.MrcExtendedListPrice)",
    description: "Rolled up from all item recurring extended list prices.",
  },
  "Total Recurring Cost": {
    level: "Total",
    formula: "Totals.MrcCost = Sum(Item.MrcExtendedCost)",
    description: "Rolled up from all item recurring extended costs.",
  },
  "Total Recurring Gross Margin Percent": {
    level: "Total",
    formula: "Totals.MrcGrossMarginPercent = 100 × (Totals.MrcNetPrice − Totals.MrcCost) / Totals.MrcNetPrice",
    description: "Total recurring gross margin as a percentage of total recurring net price.",
  },
  "Total Average Recurring Product Discount Percent": {
    level: "Total",
    formula: "Totals.AvgMrcProductDiscountPercent = 100 × Totals.MrcProductDiscountAmount / Totals.MrcListPrice",
    description: "Weighted average recurring discount percent across all items.",
  },
  "Total Recurring Product Discount Amount": {
    level: "Total",
    formula: "Totals.MrcProductDiscountAmount = Totals.MrcListPrice − Totals.MrcNetPrice",
    description: "Total recurring discount amount: total recurring list price minus total recurring net price.",
  },

  // ── Contract / Special ─────────────────────────────────────────────

  "Item Contract End Date": {
    level: "Item",
    formula: "Derived from contract start date + contract duration",
    description: "Calculates the contract end date based on start date and duration fields.",
  },
  "Item Estimated Total Contract Value": {
    level: "Item",
    formula: "Item.TCV = Item.OneTimeAmount + (Item.RecurringAmount × ContractPeriods)",
    description: "Estimated total contract value: one-time amount plus recurring amount over contract periods.",
  },
  "Product Type Estimated Total Contract Value": {
    level: "Product Type",
    formula: "PT.TCV = Sum(Item.EstimatedTotalContractValue)",
    description: "Rolled up from item estimated total contract values within this product type.",
  },
  "Total Estimated Total Contract Value": {
    level: "Total",
    formula: "Totals.TCV = Sum(Item.EstimatedTotalContractValue)",
    description: "Rolled up from all item estimated total contract values.",
  },
};

/**
 * Look up formula info for a system calculation by name.
 * @param {string} calcName
 * @returns {{ level: string, formula: string, description: string } | null}
 */
export function getFormulaInfo(calcName) {
  return FORMULAS[calcName] || null;
}
